import { Button, DrawerBody, DrawerFooter, DrawerHeader, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { assertType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getRealType } from '@pelicargo/utils';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { RuleType } from '../../../../api/src/lib/kysley/enums';
import { RuleModel } from '../../../../api/src/lib/zod-prisma';
import { trpc } from '../../config/trpc';
import { useDiscriminatedUnion } from '../../hooks/useDiscriminatedUnion';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { BaseDrawer } from '../BaseDrawer';
import { DynamicForm } from '../DynamicForm';
import { Label } from '../LabeledText';
import { SimpleForm } from '../SimpleForm';
import { TypesenseSelect } from '../TypesenseSelect';

// 😎
const baseSchema = RuleModel.omit({
  updated_at: true,
  created_at: true,
});

const discriminatedSchema = z.discriminatedUnion('type', [
  // CAN_SET_QUOTE_TO_IN_PROGRESS
  baseSchema
    .pick({
      id: true,
      provider_id: true,
      account_id: true,

      must_have_all_line_items_filled: true,
      min_num_quote_variants: true,
      width_max: true,
      length_max: true,
      height_max: true,
      chargeable_weight_max: true,
      required_handling_types: true,
      required_cargo_types: true,
      must_be_known_shipper: true,
      required_rate_categories: true,
      must_have_supported_account_type: true,
    })
    .extend({
      type: z.literal(RuleType.CAN_SET_QUOTE_TO_IN_PROGRESS),
    }),
  // CAN_EXTEND_ETT
  baseSchema
    .pick({
      id: true,
      provider_id: true,
      account_id: true,

      ett_segment_increase: true,
      aircraft_type_sequence: true,
    })
    .extend({
      type: z.literal(RuleType.CAN_EXTEND_ETT),
    }),
  // DEFAULT_SPOT_VALIDITY
  baseSchema
    .pick({
      id: true,
      provider_id: true,
      account_id: true,

      hours_of_validity: true,
    })
    .extend({
      type: z.literal(RuleType.DEFAULT_SPOT_VALIDITY),
    }),
  // DIMENSION_RESTRICTIONS
  baseSchema
    .pick({
      id: true,
      account_id: true,
      provider_id: true,

      required_handling_types: true,
      width_max: true,
      length_max: true,
      height_max: true,
    })
    .extend({
      type: z.literal(RuleType.DIMENSION_RESTRICTIONS),
    }),
  // WEIGHT_SELECTION_FOR_SURCHARGE
  baseSchema
    .pick({
      id: true,
      account_id: true,
      provider_id: true,

      required_surcharge_categories: true,
      required_surcharge_weight_type: true,
    })
    .extend({
      type: z.literal(RuleType.WEIGHT_SELECTION_FOR_SURCHARGE),
    }),
  // AWB_BOOKING_RESTRICTIONS
  baseSchema
    .pick({
      id: true,
      account_id: true,
      provider_id: true,

      must_have_awb_for_booking: true,
      must_allow_dynamic_weight_break: true,
      must_have_supported_account_type: true,
    })
    .extend({
      type: z.literal(RuleType.AWB_BOOKING_RESTRICTIONS),
    }),

  // DYNAMIC_WEIGHT_BREAK_RESTRICTIONS
  baseSchema
    .pick({
      id: true,
      account_id: true,
      provider_id: true,

      must_allow_dynamic_weight_break: true,
      must_have_supported_account_type: true,
    })
    .extend({
      type: z.literal(RuleType.DYNAMIC_WEIGHT_BREAK_RESTRICTIONS),
    }),
]);

// If this throws an error, the schema is not up to date. Ensure all RuleTypes are specified.
assertType('' as z.infer<typeof discriminatedSchema>['type'], '' as RuleType);

type Props = {
  ruleId?: number | null;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
};

export const RuleDataDrawer = ({ ruleId, isOpen, onClose, refetch }: Props) => {
  const methods = useForm({
    resolver: zodResolver(discriminatedSchema),
  });

  const schema = useDiscriminatedUnion(
    discriminatedSchema,
    methods.watch,
    Object.values(baseSchema._def.shape().type._def.values)[0],
  );

  const { data } = trpc.getRule.useQuery(
    {
      id: ruleId,
    },
    {
      enabled: !!ruleId,
    },
  );

  const { mutateAsync: upsert, isLoading } = trpc.upsertRule.useMutation();

  useEffect(() => {
    if (!ruleId) {
      methods.reset(
        Object.keys(baseSchema.shape).reduce((agg, v) => {
          agg[v] = getRealType(baseSchema.shape[v])[0] === 'ZodArray' ? [] : null;
          return agg;
        }, {}),
      );
    } else {
      methods.reset(data);
    }
  }, [data, ruleId, methods]);

  const submit = async () => {
    await upsert(methods.getValues());
    onClose();
    refetch();
  };

  const accountId = methods.watch('account_id');

  return (
    <BaseDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerHeader>
        <Heading variant="h4">Edit Rule</Heading>
      </DrawerHeader>
      <DrawerBody>
        <SimpleForm {...(methods as any)} submitOnBlur={false} handleSubmit={methods.handleSubmit(submit)}>
          <Stack spacing="4">
            <DynamicForm
              def={schema.omit({ id: true })._def}
              overrides={{
                provider_id: <AirlineInput name="provider_id" label="Airline" />,
                account_id: (
                  <Flex flexDir="column" w="full">
                    <Label label="Account" />
                    <TypesenseSelect
                      onChange={({ value }) => {
                        methods.setValue('account_id', value);
                      }}
                      value={{
                        value: accountId,
                        name_label: accountId,
                        label: `${accountId ?? ''}`,
                      }}
                      name="account_id"
                      collection="accounts"
                      queryBy="name"
                      documentLabel="name"
                      documentValue="account_id"
                      suffixWithValue
                      isMulti={false}
                    />
                  </Flex>
                ),
              }}
            />
          </Stack>
        </SimpleForm>
      </DrawerBody>
      <DrawerFooter>
        <Stack direction="row" justify={ruleId ? 'space-between' : 'right'} align="center" w="full" spacing="2">
          <Show if={!!ruleId}>
            <Text>ID: {ruleId}</Text>
          </Show>
          <Button type="submit" colorScheme="brand" onClick={submit} isLoading={isLoading}>
            Submit
          </Button>
        </Stack>
      </DrawerFooter>
    </BaseDrawer>
  );
};
